import { DragTarget, isOrderedDragTarget } from '@headless-tree/core';
import { useCallback } from 'react';

import { ForeignTreeItem, readTreeDragItems } from './foreign-drag';
import { InternalTreeItem, TreeItem, TreeItemID } from './types';

export const useOnForeignDrop = (
  items: Record<TreeItemID, InternalTreeItem>,
  callback?: (droppedItems: ForeignTreeItem[], updatedTarget: TreeItem) => void,
) =>
  useCallback(
    (dataTransfer: DataTransfer, target: DragTarget<InternalTreeItem>) => {
      if (!callback) {
        return;
      }

      const droppedItems = readTreeDragItems(dataTransfer);
      if (!droppedItems.length) {
        return;
      }

      const parent = target.item.getItemData();
      const droppedIds = new Set(droppedItems.map(({ sourceId }) => sourceId));
      const children = (parent.children ?? []).map(
        (pathId) => items[pathId]?.sourceId ?? pathId,
      );
      const index = isOrderedDragTarget(target)
        ? target.insertionIndex
        : children.length;

      // An id already in this parent moves to the drop position instead of duplicating.
      const removedBefore = children
        .slice(0, index)
        .filter((id) => droppedIds.has(id)).length;
      const remaining = children.filter((id) => !droppedIds.has(id));
      const safeIndex = Math.min(
        Math.max(index - removedBefore, 0),
        remaining.length,
      );
      remaining.splice(safeIndex, 0, ...droppedIds);

      callback(droppedItems, {
        id: parent.sourceId,
        name: parent.name,
        type: parent.type,
        isFolder: parent.isFolder,
        children: remaining,
      });
    },
    [items, callback],
  );
